import { createContext, useContext, useState, useEffect } from 'react';
import { useEventContext } from './EventContext';
import * as MetaPixel from 'analytics/metaPixel';
import { googleAnalytics } from 'services/googleAnalytics';

const StorefrontContext = createContext(null);

export const useStorefrontContext = () => {
    const context = useContext(StorefrontContext);

    if (!context) throw new Error('must wrap with StorefrontProvider');

    return context;
};

export const StorefrontProvider = ({ children }) => {
    const { products, bundles, event } = useEventContext();

    const [state, setState] = useState('products'); // products, checkout, confirmation
    const [productCart, setProductCart] = useState(products || []);
    const [bundleCart, setBundleCart] = useState(bundles || []);
    const [email, setEmail] = useState(null); // Email to send tickts to. Set after purchase, viewed in PurchaseSuccess page
    const [discount, setDiscount] = useState(null); // Discount applied to purchase. Set after purchase, viewed in PurchaseSuccess page
    const [ticketCount, setTicketCount] = useState(null); // PDF tickets in dataURI form. Set after successful purchase. Can be downloaded from PurchaseSuccess page
    const [tranId, setTranId] = useState(null); // ID of successful transaction. Used to make a ticket_activty log entry when customer downloads tickts
    const [appleWalletURL, setAppleWalletURL] = useState(null);
    const [googleWalletURL, setGoogleWalletURL] = useState(null);
    const [googleCalendarURL, setGoogleCalendarURL] = useState(null);
    const [outlookCalendarURL, setOutlookCalendarURL] = useState(null);
    const [officeCalendarURL, setOfficeCalendarURL] = useState(null);
    const [calendarURL, setCalendarURL] = useState(null);
    const [receiptData, setReceiptData] = useState(null);

    // When initializing the Storefront, set the product and bundle carts cause they may be null at first
    useEffect(() => {
        if (products) {
            setProductCart(products);
        }
        if (bundles) {
            setBundleCart(bundles);
        }
    }, [products, bundles]);

    const handleCheckoutOpen = () => {
        setState('checkout');
        if (event.metaPixelId) {
            MetaPixel.initiateCheckout(event.metaPixelId);
        }
    };

    const handleCheckoutClose = () => {
        setState('products');

        // Reset product cart quantities to 0
        setProductCart(productCart.map((product) => ({ ...product, qty: 0 })));
        setBundleCart(bundleCart.map((bundle) => ({ ...bundle, qty: 0 })));

        // Clear seats.io session from sessionStorage
        // seats.io stores the holdToken with key 'seatsio-session-{eventKey}'
        Object.keys(sessionStorage).forEach((key) => {
            if (key.startsWith('seatsio')) {
                sessionStorage.removeItem(key);
            }
        });
    };

    const handleAddProduct = (id) => {
        setProductCart(
            productCart.map((product) => {
                if (product.id === id) {
                    googleAnalytics.addToCart(
                        id,
                        product.name,
                        product.qty + 1,
                        product.price,
                        event.currency,
                    );
                    // match
                    if (product.qty < product.minPurchase) {
                        // Bump qty up to minPurchase
                        return { ...product, qty: product.minPurchase };
                    }
                    if (product.qty < product.maxPurchase && product.qty < product.available) {
                        // Bump qty up by 1
                        return { ...product, qty: product.qty + 1 };
                    }
                }

                // no match or max qty reached
                return product;
            }),
        );
    };

    const handleRemoveProduct = (id) => {
        setProductCart(
            productCart.map((product) => {
                if (product.id === id) {
                    // match
                    if (product.qty === product.minPurchase) {
                        // Bump qty down to 0
                        return { ...product, qty: 0 };
                    }
                    if (product.qty > 0) {
                        // Bump qty down by 1
                        return { ...product, qty: product.qty - 1 };
                    }
                }

                // no match or already at 0 qty
                return product;
            }),
        );
    };

    const handleAddBundle = (id) => {
        setBundleCart(
            bundleCart.map((bundle) => {
                if (bundle.id === id) {
                    googleAnalytics.addToCart(
                        id,
                        bundle.name,
                        bundle.qty + 1,
                        bundle.price,
                        event.currency,
                    );
                    // match
                    if (bundle.qty < bundle.minPurchase) {
                        // Bump qty up to minPurchase
                        return { ...bundle, qty: bundle.minPurchase };
                    }
                    if (bundle.qty < bundle.maxPurchase && bundle.qty < bundle.available) {
                        // Bump qty up by 1
                        return { ...bundle, qty: bundle.qty + 1 };
                    }
                }

                // no match or max qty reached
                return bundle;
            }),
        );
    };

    const handleRemoveBundle = (id) => {
        setBundleCart(
            bundleCart.map((bundle) => {
                if (bundle.id === id) {
                    // match
                    if (bundle.qty === bundle.minPurchase) {
                        // Bump qty down to 0
                        return { ...bundle, qty: 0 };
                    }
                    if (bundle.qty > 0) {
                        // Bump qty down by 1
                        return { ...bundle, qty: bundle.qty - 1 };
                    }
                }

                // no match or already at 0 qty
                return bundle;
            }),
        );
    };

    const handlePurchase = (
        email,
        discount,
        ticketCount,
        tranId,
        appleWalletURL,
        googleWalletURL,
        googleCalendarURL,
        outlookCalendarURL,
        officeCalendarURL,
        calendarURL,
        receiptData,
    ) => {
        setState('confirmation');
        setEmail(email);
        setDiscount(discount);
        setTicketCount(ticketCount);
        setTranId(tranId);
        setAppleWalletURL(appleWalletURL);
        setGoogleWalletURL(googleWalletURL);
        setGoogleCalendarURL(googleCalendarURL);
        setOutlookCalendarURL(outlookCalendarURL);
        setOfficeCalendarURL(officeCalendarURL);
        setCalendarURL(calendarURL);
        setReceiptData(receiptData);
    };

    const handleContinueShopping = () => {
        setState('products');
        setProductCart(products);
        setBundleCart(bundles);
        setEmail(null);
        setDiscount(null);
        setTicketCount(null);
        setTranId(null);
        setAppleWalletURL(null);
        setGoogleWalletURL(null);
        setGoogleCalendarURL(null);
        setOutlookCalendarURL(null);
        setOfficeCalendarURL(null);
        setCalendarURL(null);
        setReceiptData(null);

        // Clear seats.io session from sessionStorage so a fresh session starts next time
        Object.keys(sessionStorage).forEach((key) => {
            if (key.startsWith('seatsio')) {
                sessionStorage.removeItem(key);
            }
        });
    };

    return (
        <StorefrontContext.Provider
            value={{
                event,
                handleAddProduct,
                handleRemoveProduct,
                handleAddBundle,
                handleRemoveBundle,
                handlePurchase,
                handleContinueShopping,
                email,
                discount,
                ticketCount,
                tranId,
                appleWalletURL,
                googleWalletURL,
                googleCalendarURL,
                outlookCalendarURL,
                officeCalendarURL,
                calendarURL,
                receiptData,
                productCart,
                bundleCart,
                state,
                setState,
                setDiscount,
                handleCheckoutOpen,
                handleCheckoutClose,
            }}
        >
            {children}
        </StorefrontContext.Provider>
    );
};
